import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import { MutationResponse } from 'services/Engagement/types'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { GenericResponse, TableRequestV3, TableResponse } from 'services/types'
import {
  CreateEmployeeRequest,
  CreateEmployeeResponse,
  GetEmployeeRequest,
  GetEmployeeResponse,
} from './types'
import { AttachmentType } from '@evrekadev/evreka-ui-components'
import { isDummyClient } from 'utils'

export const employeeApi = createApi({
  reducerPath: 'employeeApi',
  tagTypes: [
    'Employees',
    'EmployeeDetail',
    'WorkDetails',
    'PayDetails',
    'Contact',
    'Attachments',
    'Notes',
    'Works',
    'Leaves',
    'WorkPlans',
    'EmployeeAttachmentList',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/employee-management/'}`,
  }),
  endpoints: (builder) => ({
    createEmployee: builder.mutation<CreateEmployeeResponse, CreateEmployeeRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/employee/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Employees'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {}
      },
    }),
    updateEmployee: builder.mutation<
      Omit<CreateEmployeeResponse, 'id'> & { employee_id: string },
      Omit<CreateEmployeeRequest, 'name' | 'surname'> & {
        employeeId: string
        first_name: string
        last_name: string
      }
    >({
      query: ({ clientId, employeeId, ...data }) => ({
        url: `clients/${clientId}/employee/${employeeId}/`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Employees', 'EmployeeDetail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {}
      },
    }),
    getEmployeeDetail: builder.query<GetEmployeeResponse, GetEmployeeRequest>({
      query: ({ clientId, employeeId }) => ({
        url: `clients/${clientId}/employee/${employeeId}/`,
        method: 'GET',
      }),
      providesTags: ['EmployeeDetail'],
    }),
    getEmployeeList: builder.query<TableResponse<{}>, TableRequestV3 & { opsClientId?: number }>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/employee/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['Employees', 'EmployeeDetail'],
      transformResponse: (response: TableResponse<{}>, meta, arg) => {
        // Check if this client should use dummy data
        if (!isDummyClient(arg.opsClientId)) {
          return response
        }

        // Add provider columns at the end
        const providerColumns = [
          {
            cellType: 'DEFAULT',
            header: 'Leverancierstype',
            accessor: 'provider_type',
            id: 'provider_type',
            filterType: 'TEXT',
            disableFilters: false,
            disableSortBy: false,
            isHidden: false,
            hidable: true,
            isVisible: true,
            filterOptions: [],
          },
          {
            cellType: 'DEFAULT',
            header: 'Leverancier',
            accessor: 'provider',
            id: 'provider',
            filterType: 'TEXT',
            disableFilters: false,
            disableSortBy: false,
            isHidden: false,
            hidable: true,
            isVisible: true,
            filterOptions: [],
          },
        ]

        // Enhanced columns with provider information
        const enhancedColumns = [...response.columns, ...providerColumns]

        // Add provider data to each employee based on name
        const enhancedData = response.data.map((employee) => {
          const employeeName = (employee.name as string) || ''
          const firstLetter = employeeName.charAt(0).toUpperCase()

          let providerType = 'Intern'
          let provider = '-'

          if (firstLetter === 'A' || firstLetter === 'a') {
            providerType = 'Gecontracteerd'
            provider = '247 Drive'
          } else if (
            firstLetter === 'D' ||
            firstLetter === 'N' ||
            firstLetter === 'd' ||
            firstLetter === 'n'
          ) {
            providerType = 'Inhuur'
            provider = 'Flexteam'
          }

          return {
            ...employee,
            provider_type: providerType,
            provider: provider,
          }
        })

        return {
          ...response,
          columns: enhancedColumns,
          data: enhancedData,
        }
      },
    }),
    deleteEmployee: builder.mutation<MutationResponse, { clientId: string; employeeId: string }>({
      query: ({ clientId, employeeId }) => ({
        url: `clients/${clientId}/employee/${employeeId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Employees', 'EmployeeDetail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {}
      },
    }),
    updateEmployeeStatus: builder.mutation<
      GenericResponse & { employee_id: string },
      { clientId: string; employeeId: string; status: number }
    >({
      query: ({ clientId, employeeId, ...data }) => ({
        url: `clients/${clientId}/employee/${employeeId}/`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['Employees', 'EmployeeDetail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {}
      },
    }),
    getEmployeeAttachmentList: builder.query<
      { data: AttachmentType[] } & GenericResponse,
      { clientId: string; employeeId: string }
    >({
      query: ({ clientId, employeeId }) => ({
        url: `clients/${clientId}/employee/${employeeId}/attachment/`,
        method: 'GET',
      }),
      providesTags: ['EmployeeAttachmentList'],
    }),
    employeeAttachmentUpsert: builder.mutation<
      GenericResponse & { employee_id: string },
      { clientId: string; employeeId: string; body: FormData }
    >({
      query: ({ clientId, employeeId, body }) => ({
        url: `clients/${clientId}/employee/${employeeId}/attachment/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['EmployeeAttachmentList'],
    }),
    addEmployeeProfilePhoto: builder.mutation<
      GenericResponse & { employee_id: string },
      { clientId: string; employeeId: string; body: FormData }
    >({
      query: ({ clientId, employeeId, body }) => ({
        url: `clients/${clientId}/employee/${employeeId}/profile_picture/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['EmployeeDetail'],
    }),
  }),
})

export const {
  useCreateEmployeeMutation,
  useGetEmployeeDetailQuery,
  useGetEmployeeListQuery,
  useDeleteEmployeeMutation,
  useUpdateEmployeeMutation,
  useUpdateEmployeeStatusMutation,
  useGetEmployeeAttachmentListQuery,
  useEmployeeAttachmentUpsertMutation,
  useAddEmployeeProfilePhotoMutation,
} = employeeApi
